import React, { useState } from "react";
import { CButton, CCard, CCardBody, CCol, CContainer, CRow } from "@coreui/react-pro";
import { BRAND_COLOR, DARK_COLOR, SITE } from "../../config/site";
import { useSeo } from "../../hooks/useSeo";

interface ContactUsProps { }

const DELETE_ACCOUNT_TOPIC = "Quiero eliminar mi cuenta";

const topics = [
    "Tengo un problema con un pedido",
    "Tengo una pregunta sobre la app",
    DELETE_ACCOUNT_TOPIC,
    "Quiero registrar mi negocio",
    "Otro tema",
];

export const ContactUS: React.FC<ContactUsProps> = () => {
    useSeo({
        title: "Contacto y ayuda | DomicilApp",
        description: "¿Tienes un problema con un pedido o una pregunta sobre DomicilApp? Escríbenos por WhatsApp, al correo soporte@domicilapp.com o llámanos.",
    });
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        topic: topics[0],
        order: "",
        message: ""
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // No hay endpoint de contacto: se abre el correo del usuario con el mensaje ya escrito.
    // El asunto "Eliminar mi cuenta" es el que pide la política de privacidad.
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const subject = formData.topic === DELETE_ACCOUNT_TOPIC ? "Eliminar mi cuenta" : formData.topic;
        const details = [
            `Nombre: ${formData.name}`,
            formData.phone && `Celular: ${formData.phone}`,
            formData.order && `Número de pedido: ${formData.order}`,
        ].filter(Boolean).join("\n");
        const body = `${details}\n\n${formData.message}`;
        window.location.href = `mailto:${SITE.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <div className="contact-page bg-light py-5">
            <CContainer className="my-lg-4">

                <div className="text-center mb-5">
                    <h1 className="fw-bold display-6 text-dark">¿En qué te podemos ayudar?</h1>
                    <p className="fs-5 text-secondary mx-auto mb-0" style={{ maxWidth: "640px" }}>
                        Escríbenos por el medio que prefieras. Si es sobre un pedido, ten a mano su número para ayudarte más rápido.
                    </p>
                </div>

                <CRow className="g-4 mb-4">
                    <CCol md={4}>
                        <a href={SITE.whatsappUrl} target="_blank" rel="noreferrer" className="d-block h-100 p-4 rounded-4 bg-white shadow-sm text-decoration-none">
                            <i className="bi bi-whatsapp fs-2" style={{ color: "#25D366" }}></i>
                            <h5 className="fw-bold text-dark mt-2 mb-1">WhatsApp</h5>
                            <p className="text-secondary mb-0">La forma más rápida de resolver algo de tu pedido.</p>
                        </a>
                    </CCol>
                    <CCol md={4}>
                        <a href={`mailto:${SITE.supportEmail}`} className="d-block h-100 p-4 rounded-4 bg-white shadow-sm text-decoration-none">
                            <i className="bi bi-envelope fs-2" style={{ color: BRAND_COLOR }}></i>
                            <h5 className="fw-bold text-dark mt-2 mb-1">Correo</h5>
                            <p className="text-secondary mb-0">{SITE.supportEmail}</p>
                        </a>
                    </CCol>
                    <CCol md={4}>
                        <a href={SITE.phoneHref} className="d-block h-100 p-4 rounded-4 bg-white shadow-sm text-decoration-none">
                            <i className="bi bi-telephone fs-2" style={{ color: DARK_COLOR }}></i>
                            <h5 className="fw-bold text-dark mt-2 mb-1">Teléfono</h5>
                            <p className="text-secondary mb-0">{SITE.phone}</p>
                        </a>
                    </CCol>
                </CRow>

                <CRow className="g-4 align-items-stretch">
                    <CCol lg={7}>
                        <CCard className="border-0 shadow-sm rounded-4 h-100">
                            <CCardBody className="p-4 p-md-5">
                                <h3 className="fw-bold mb-1 text-dark">Escríbenos aquí</h3>
                                <p className="text-secondary mb-4">Al enviar se abrirá tu correo con el mensaje listo para mandarlo.</p>

                                <form onSubmit={handleSubmit}>
                                    <CRow className="g-3">
                                        <CCol md={6}>
                                            <div className="form-floating">
                                                <input type="text" className="form-control" id="inputName" name="name" placeholder="Nombre" value={formData.name} onChange={handleChange} required />
                                                <label htmlFor="inputName">Tu nombre</label>
                                            </div>
                                        </CCol>
                                        <CCol md={6}>
                                            <div className="form-floating">
                                                <input type="tel" className="form-control" id="inputPhone" name="phone" placeholder="Celular" value={formData.phone} onChange={handleChange} />
                                                <label htmlFor="inputPhone">Celular (opcional)</label>
                                            </div>
                                        </CCol>
                                        <CCol md={7}>
                                            <div className="form-floating">
                                                <select className="form-select" id="inputTopic" name="topic" value={formData.topic} onChange={handleChange}>
                                                    {topics.map((topic) => <option key={topic}>{topic}</option>)}
                                                </select>
                                                <label htmlFor="inputTopic">Tema</label>
                                            </div>
                                        </CCol>
                                        <CCol md={5}>
                                            <div className="form-floating">
                                                <input type="text" className="form-control" id="inputOrder" name="order" placeholder="Pedido" value={formData.order} onChange={handleChange} />
                                                <label htmlFor="inputOrder">N.º de pedido (opcional)</label>
                                            </div>
                                        </CCol>
                                        <CCol md={12}>
                                            <div className="form-floating">
                                                <textarea className="form-control" placeholder="Mensaje" id="inputMessage" name="message" style={{ height: "140px" }} value={formData.message} onChange={handleChange} required></textarea>
                                                <label htmlFor="inputMessage">Cuéntanos qué pasó</label>
                                            </div>
                                        </CCol>
                                        <CCol md={12} className="mt-4">
                                            <CButton type="submit" size="lg" className="w-100 fw-bold py-3 text-white border-0" style={{ backgroundColor: BRAND_COLOR }}>
                                                Enviar mensaje
                                            </CButton>
                                        </CCol>
                                    </CRow>
                                </form>
                            </CCardBody>
                        </CCard>
                    </CCol>

                    <CCol lg={5}>
                        <CCard className="border-0 shadow-sm rounded-4 overflow-hidden h-100">
                            <iframe
                                title="Zona de servicio de DomicilApp: Chiquinquirá, Boyacá"
                                src="https://www.google.com/maps?q=Chiquinquir%C3%A1%2C%20Boyac%C3%A1&z=14&output=embed"
                                width="100%"
                                style={{ border: 0, minHeight: "320px", flexGrow: 1 }}
                                loading="lazy"
                                allowFullScreen
                            ></iframe>
                            <div className="p-3 text-secondary small">
                                <i className="bi bi-geo-alt me-1"></i>Hacemos domicilios en todo {SITE.serviceArea}. No tenemos oficina de atención al público: escríbenos por WhatsApp o al correo.
                            </div>
                        </CCard>
                    </CCol>
                </CRow>
            </CContainer>
        </div>
    );
};
